import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, useTheme, api, API } from "../App";
import {
  Copy,
  Check,
  SkipForward,
  RefreshCw,
  Sparkles,
  BookOpen,
  Heart,
  Image,
  ChevronLeft,
  ChevronRight,
  Pencil,
  X,
  Download,
  Settings,
  Star,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import AppShell from "../components/AppShell";

const TOPIC_LABELS = {
  "ai-ml": "AI & ML",
  "cloud": "Cloud",
  "devops": "DevOps",
  "security": "Security",
  "web-dev": "Web Dev",
  "mobile": "Mobile",
  "data-science": "Data Science",
  "blockchain": "Blockchain",
  "engineering": "Engineering",
  "product": "Product",
  "ux-design": "UX Design",
  "startup": "Startup",
  "leadership": "Leadership",
  "career": "Career",
  "open-source": "Open Source",
  "programming": "Programming",
};

const cleanPostContent = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")   // **bold** → plain
    .replace(/\*(.*?)\*/g, "$1")        // *italic* → plain
    .replace(/^---+\s*VARIANT.*$/gm, "") // --- VARIANT: ... --- headers
    .replace(/^---+$/gm, "")            // standalone --- separators
    .replace(/^#{1,6}\s+/gm, "")        // ## headings → plain
    .replace(/^\s*[-*]\s+/gm, "• ")     // - bullets → •
    .replace(/\n{3,}/g, "\n\n")         // collapse excess blank lines
    .trim();
};

const IMAGE_TYPE_LABELS = {
  infographic: "Infographic",
  carousel: "Carousel",
  dalle: "AI Image",
};

const PostCard = ({ post, user, onCopy, onMarkUsed, onSkip, onEdit, onStar, copiedId, starredIds }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const carouselData = (() => {
    if (!post.carousel_data) return null;
    if (typeof post.carousel_data === "object") return post.carousel_data;
    try { return JSON.parse(post.carousel_data); } catch { return null; }
  })();

  const content = cleanPostContent(post.variant_content);
  const shouldTruncate = content.length > 400;
  const displayContent = shouldTruncate && !isExpanded
    ? content.substring(0, 400)
    : content;

  const handleEditStart = () => {
    setEditContent(post.variant_content);
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditContent("");
  };

  const handleEditSave = async () => {
    if (!editContent.trim()) return;
    setIsSaving(true);
    try {
      await api.patch(`/posts/${post.id}`, { variant_content: editContent });
      onEdit(post.id, editContent);
      setIsEditing(false);
      toast.success("Post updated");
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Image state
  const backendUrl = API.replace(/\/api$/, "");
  const [imageUrl, setImageUrl] = useState(
    post.image_path ? `${backendUrl}/static/images/${post.image_path}` : null
  );
  const [carouselUrls, setCarouselUrls] = useState(
    post.carousel_paths ? post.carousel_paths.map(p => `${backendUrl}/static/images/${p}`) : []
  );
  const [imageStatus, setImageStatus] = useState(post.image_status || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  const handleGenerateImage = async () => {
    setIsGenerating(true);
    try {
      const res = await api.post(`/posts/${post.id}/generate-image`);
      if (res.data.image_url) setImageUrl(res.data.image_url);
      if (res.data.carousel_urls) setCarouselUrls(res.data.carousel_urls);
      setImageStatus("done");
    } catch {
      toast.error("Image generation failed");
      setImageStatus("failed");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (post.image_type === "carousel") {
        const { jsPDF } = await import("jspdf");
        const PAGE_W = 216, PAGE_H = 270; // preserves 1080:1350 (4:5) aspect ratio
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: [PAGE_W, PAGE_H] });
        for (let i = 0; i < carouselUrls.length; i++) {
          if (i > 0) doc.addPage([PAGE_W, PAGE_H], "portrait");
          const resp = await fetch(carouselUrls[i]);
          if (!resp.ok) throw new Error(`Slide ${i + 1} failed`);
          const blob = await resp.blob();
          const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(blob);
          });
          doc.addImage(dataUrl, "PNG", 0, 0, PAGE_W, PAGE_H);
        }
        doc.save(`carousel_${post.id}.pdf`);
      } else {
        const resp = await fetch(imageUrl);
        if (!resp.ok) throw new Error("Download failed");
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = imageUrl.split("/").pop() || `post_${post.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }
      toast.success("Download started");
    } catch {
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="bg-card rounded-lg border border-border hover:border-primary/30 shadow-pw-ambient transition-all duration-300 overflow-hidden min-w-0"
      data-testid={`post-card-${post.id}`}
    >
      {/* PostWriter card header */}
      <div className="flex items-start gap-3 px-4 pt-3 pb-2">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full shrink-0" />
        ) : (
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-lg shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-headline font-semibold text-sm text-foreground leading-tight">{user?.name || "You"}</p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5 truncate">
            {TOPIC_LABELS[post.topic] || post.topic}
            {post.angle && ` \u00b7 ${post.angle}`}
          </p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {/* AI quality badge */}
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-label font-semibold">
              <Sparkles className="w-2.5 h-2.5" />
              AI Refined
            </span>
            {post.is_trending && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-500 text-[10px] font-label font-semibold">
                🔥 Trending
              </span>
            )}
            {post.story_potential_score != null && (
              <div className="group/tip relative flex items-center gap-0.5 text-[10px] font-medium text-amber-500 dark:text-amber-400 cursor-default">
                {post.story_potential_score}/10
                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md bg-popover text-popover-foreground text-xs px-2 py-1 shadow-md opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-10">
                  Story Potential
                </span>
              </div>
            )}
            {post.relatability_score != null && (
              <div className="group/tip2 relative flex items-center gap-0.5 text-[10px] font-medium text-rose-500 dark:text-rose-400 cursor-default">
                <Heart className="w-2.5 h-2.5" />
                {post.relatability_score}/10
                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md bg-popover text-popover-foreground text-xs px-2 py-1 shadow-md opacity-0 group-hover/tip2:opacity-100 transition-opacity duration-150 z-10">
                  Relatability
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-2">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              className="w-full min-h-[200px] rounded-md border border-border bg-background text-foreground text-sm leading-relaxed p-2 resize-y focus:outline-none focus:ring-2 focus:ring-primary"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleEditCancel}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={isSaving || !editContent.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {isSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          </div>
        ) : post.content_format === "carousel" && carouselData ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded-full">Carousel</span>
              <span className="text-xs text-muted-foreground">{carouselData.slides?.length || 0} slides</span>
            </div>
            <p className="font-semibold text-foreground text-sm">{carouselData.hook_title}</p>
            {carouselData.hook_subtitle && (
              <p className="text-xs text-muted-foreground italic">{carouselData.hook_subtitle}</p>
            )}
            <div className="space-y-1.5 mt-2">
              {(isExpanded ? carouselData.slides : carouselData.slides?.slice(0, 3))?.map((slide, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs font-bold text-primary bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{slide.slide_number || i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground leading-tight">{slide.title}</p>
                    <p className="text-xs text-muted-foreground leading-snug">{slide.body}</p>
                  </div>
                </div>
              ))}
              {!isExpanded && carouselData.slides?.length > 3 && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-muted-foreground text-xs hover:text-primary hover:underline ml-7"
                >
                  +{carouselData.slides.length - 3} more slides...
                </button>
              )}
              {isExpanded && carouselData.slides?.length > 3 && (
                <button
                  onClick={() => setIsExpanded(false)}
                  className="text-muted-foreground text-xs hover:text-primary hover:underline ml-7"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="text-foreground whitespace-pre-wrap leading-relaxed text-sm break-words">
              {displayContent}
              {shouldTruncate && !isExpanded && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-muted-foreground hover:text-primary hover:underline ml-0.5"
                  data-testid={`expand-btn-${post.id}`}
                >
                  ...see more
                </button>
              )}
            </p>
            {shouldTruncate && isExpanded && (
              <button
                onClick={() => setIsExpanded(false)}
                className="text-muted-foreground text-sm hover:text-primary hover:underline mt-1"
                data-testid={`expand-btn-${post.id}`}
              >
                Show less
              </button>
            )}
          </>
        )}
      </div>

      {/* Takeaway — PostWriter editorial style */}
      {post.takeaway && (
        <div className="mx-4 mb-2 p-3 bg-muted rounded-md border-l-2 border-l-primary">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3 text-primary" />
            <p className="text-[10px] text-primary font-label font-semibold uppercase tracking-widest">Key Takeaway</p>
          </div>
          <p className="text-sm text-foreground leading-relaxed">{post.takeaway}</p>
        </div>
      )}

      {/* Image Section */}
      {post.image_type && (
        <div className="mt-1">
          {imageStatus === "done" && post.image_type !== "carousel" && imageUrl && (
            <img
              src={imageUrl}
              alt={IMAGE_TYPE_LABELS[post.image_type] || "Post visual"}
              className="w-full border-y border-border object-cover"
            />
          )}
          {imageStatus === "done" && post.image_type === "carousel" && carouselUrls.length > 0 && (
            <div className="relative">
              <img
                src={carouselUrls[carouselIdx]}
                alt={`Slide ${carouselIdx + 1} of ${carouselUrls.length}`}
                className="w-full border-y border-border object-cover"
              />
              {carouselUrls.length > 1 && (
                <>
                  <button
                    onClick={() => setCarouselIdx(i => Math.max(0, i - 1))}
                    disabled={carouselIdx === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCarouselIdx(i => Math.min(carouselUrls.length - 1, i + 1))}
                    disabled={carouselIdx === carouselUrls.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="flex justify-center gap-1.5 mt-2 pb-2">
                    {carouselUrls.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCarouselIdx(i)}
                        className={`w-2 h-2 rounded-full transition-colors ${i === carouselIdx ? "bg-primary" : "bg-muted-foreground/30"}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {imageStatus !== "done" && (
            <div className="px-4 pb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowGenerateConfirm(true)}
                disabled={isGenerating || imageStatus === "generating"}
                className="w-full border-dashed"
              >
                {isGenerating || imageStatus === "generating" ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating {IMAGE_TYPE_LABELS[post.image_type]}…
                  </>
                ) : (
                  <>
                    <Image className="w-4 h-4 mr-2" />
                    Generate {IMAGE_TYPE_LABELS[post.image_type]}
                  </>
                )}
              </Button>
              <AlertDialog open={showGenerateConfirm} onOpenChange={setShowGenerateConfirm}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Generate {IMAGE_TYPE_LABELS[post.image_type]}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will generate a {IMAGE_TYPE_LABELS[post.image_type]?.toLowerCase()} for this post. This action uses AI image generation credits.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleGenerateImage}>Generate</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      )}

      {/* LinkedIn-style action bar */}
      <div className="border-t border-border mx-4" />
      <div className="flex items-center px-2 py-1">
        <button
          onClick={() => onCopy(post)}
          data-testid={`copy-btn-${post.id}`}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-colors ${
            copiedId === post.id
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          {copiedId === post.id ? (
            <Check className="w-5 h-5" />
          ) : (
            <Copy className="w-5 h-5" />
          )}
          <span className="hidden sm:inline">{copiedId === post.id ? "Copied!" : "Copy"}</span>
        </button>

        <button
          onClick={() => onMarkUsed(post)}
          data-testid={`mark-used-btn-${post.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Mark as used"
        >
          <BookOpen className="w-5 h-5" />
          <span className="hidden sm:inline">Used</span>
        </button>

        <button
          onClick={handleEditStart}
          data-testid={`edit-btn-${post.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Edit post"
        >
          <Pencil className="w-5 h-5" />
          <span className="hidden sm:inline">Edit</span>
        </button>

        {imageStatus === "done" && post.image_type && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            data-testid={`download-btn-${post.id}`}
            title={post.image_type === "carousel" ? "Download as PDF" : "Download PNG"}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            {isDownloading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            <span className="hidden sm:inline">{isDownloading ? "..." : "Download"}</span>
          </button>
        )}

        <button
          onClick={() => onSkip(post)}
          data-testid={`skip-btn-${post.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Skip"
        >
          <SkipForward className="w-5 h-5" />
          <span className="hidden sm:inline">Skip</span>
        </button>

        <button
          onClick={() => onStar(post)}
          data-testid={`star-btn-${post.id}`}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-colors ${
            starredIds?.has(post.id)
              ? "text-amber-500 hover:text-muted-foreground hover:bg-muted"
              : "text-muted-foreground hover:text-amber-500 hover:bg-muted"
          }`}
          title={starredIds?.has(post.id) ? "Unstar" : "Star"}
        >
          <Star className={`w-5 h-5 ${starredIds?.has(post.id) ? "fill-amber-500" : ""}`} />
          <span className="hidden sm:inline">{starredIds?.has(post.id) ? "Starred" : "Star"}</span>
        </button>
      </div>
    </div>
  );
};

const PostSkeleton = () => (
  <div className="bg-card rounded-lg border border-border overflow-hidden">
    <div className="flex items-start gap-3 px-4 pt-3 pb-2">
      <Skeleton className="w-12 h-12 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
    <div className="px-4 py-2 space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="border-t border-border mx-4" />
    <div className="flex gap-2 px-4 py-3">
      <Skeleton className="h-8 flex-1 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
    </div>
  </div>
);

const FeedPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [userTopics, setUserTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({ posts_used: 0, posts_available: 0 });
  const [isGenerating, setIsGenerating] = useState(false);
  const [starredIds, setStarredIds] = useState(new Set());

  const fetchPosts = useCallback(async (pageNum = 1, topic = null, append = false, section = null) => {
    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: 10 });
      if (section) params.append("section", section);
      else if (topic) params.append("topic", topic);

      const response = await api.get(`/posts?${params}`);
      const newPosts = response.data.posts;

      if (append) {
        setPosts((prev) => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setHasMore(response.data.has_more);
      setPage(pageNum);
      setIsGenerating(response.data.generating || false);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      toast.error("Failed to load posts");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);


  const fetchUserTopics = useCallback(async () => {
    try {
      const response = await api.get("/user/topics");
      setUserTopics(response.data.topics);
    } catch (error) {
      console.error("Failed to fetch user topics:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchUserTopics();
    fetchStats();
  }, [fetchPosts, fetchUserTopics, fetchStats]);

  // Poll every 10 seconds while posts are being generated
  useEffect(() => {
    if (!isGenerating || posts.length > 0) return;
    const interval = setInterval(() => {
      fetchPosts();
    }, 10000);
    return () => clearInterval(interval);
  }, [isGenerating, posts.length, fetchPosts]);

  const handleCopy = async (post) => {
    try {
      await navigator.clipboard.writeText(cleanPostContent(post.variant_content));
      setCopiedId(post.id);

      // Mark as used
      await api.post(`/posts/${post.id}/use`);

      // Remove from feed
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post copied to clipboard!");
      fetchStats();

      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy post");
    }
  };

  const handleMarkUsed = async (post) => {
    try {
      await api.post(`/posts/${post.id}/use`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post marked as used");
      fetchStats();
    } catch (error) {
      console.error("Failed to mark as used:", error);
      toast.error("Failed to mark post as used");
    }
  };

  const handleSkip = async (post) => {
    try {
      await api.post(`/posts/${post.id}/skip`);
      // Move post to end of list
      setPosts((prev) => {
        const filtered = prev.filter((p) => p.id !== post.id);
        return [...filtered, post];
      });
      toast.info("Post skipped");
    } catch (error) {
      console.error("Failed to skip:", error);
    }
  };

  const handleEditPost = (postId, newContent) => {
    setPosts((prev) =>
      prev.map((p) => p.id === postId ? { ...p, variant_content: newContent } : p)
    );
  };

  const handleStar = async (post) => {
    const isStarred = starredIds.has(post.id);
    try {
      if (isStarred) {
        await api.post(`/posts/${post.id}/unstar`);
        setStarredIds((prev) => { const next = new Set(prev); next.delete(post.id); return next; });
        toast.info("Removed from starred");
      } else {
        await api.post(`/posts/${post.id}/star`);
        setStarredIds((prev) => new Set(prev).add(post.id));
        toast.success("Post starred!");
      }
    } catch (error) {
      console.error("Failed to star/unstar:", error);
      toast.error("Failed to update star");
    }
  };

  const handleTopicFilter = (topic) => {
    setSelectedTopic(topic);
    if (topic === "__trending") {
      fetchPosts(1, null, false, "trending");
    } else if (topic === "__others") {
      fetchPosts(1, null, false, "others");
    } else {
      fetchPosts(1, topic);
    }
  };

  const handleLoadMore = () => {
    if (selectedTopic === "__trending") {
      fetchPosts(page + 1, null, true, "trending");
    } else if (selectedTopic === "__others") {
      fetchPosts(page + 1, null, true, "others");
    } else {
      fetchPosts(page + 1, selectedTopic, true);
    }
  };

  const handleRefresh = () => {
    if (selectedTopic === "__trending") {
      fetchPosts(1, null, false, "trending");
    } else if (selectedTopic === "__others") {
      fetchPosts(1, null, false, "others");
    } else {
      fetchPosts(1, selectedTopic);
    }
    fetchStats();
    toast.success("Feed refreshed");
  };

  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
  };

  const headerRight = (
    <>
      <div className="hidden sm:flex items-center gap-4 mr-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" />
          <span>{stats.posts_used} used</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          <span>{stats.posts_available} available</span>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={handleRefresh} data-testid="refresh-btn" title="Refresh feed">
        <RefreshCw className="w-5 h-5" />
      </Button>
    </>
  );

  return (
    <AppShell
      activeNav="feed"
      userTopics={userTopics}
      selectedTopic={selectedTopic}
      onTopicFilter={handleTopicFilter}
      headerRight={headerRight}
    >
      <div className="py-8 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Mobile topic chips */}
        {userTopics.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6 lg:hidden">
            {[null, ...userTopics, "__trending", "__others"].map((value) => {
              const label = value === null ? "All Topics" : value === "__trending" ? "🔥 Trending" : value === "__others" ? "Others" : (TOPIC_LABELS[value] || value);
              const active = selectedTopic === value;
              return (
                <button
                  key={value ?? "all"}
                  onClick={() => handleTopicFilter(value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold font-label transition-all ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground border border-border hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Posts */}
        {isLoading ? (
          <>
            <div className="hidden xl:flex gap-4">
              <div className="flex-1 flex flex-col gap-4">
                <PostSkeleton /><PostSkeleton />
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <PostSkeleton />
              </div>
            </div>
            <div className="flex xl:hidden flex-col gap-4">
              {[...Array(3)].map((_, i) => <PostSkeleton key={i} />)}
            </div>
          </>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            {isGenerating ? (
              <>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Generating your posts</h3>
                <p className="text-muted-foreground mb-2">
                  We're crafting personalized LinkedIn posts based on your topics and preferences.
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  This usually takes a few minutes. Refresh the page to check for new posts.
                </p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No posts available</h3>
                <p className="text-muted-foreground mb-4">
                  You've gone through all the posts for your selected topics!
                </p>
                <Button onClick={() => navigate("/settings")} className="bg-primary hover:bg-primary/90 text-white">
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Topics
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Two independent columns — cards in different columns never share row height */}
            <div className="hidden xl:flex gap-4 items-start">
              {[posts.filter((_, i) => i % 2 === 0), posts.filter((_, i) => i % 2 === 1)].map(
                (col, ci) => (
                  <div key={ci} className="flex-1 min-w-0 flex flex-col gap-4">
                    {col.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        user={user}
                        onCopy={handleCopy}
                        onMarkUsed={handleMarkUsed}
                        onSkip={handleSkip}
                        onEdit={handleEditPost}
                        onStar={handleStar}
                        copiedId={copiedId}
                        starredIds={starredIds}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
            {/* Single column on smaller screens */}
            <div className="flex xl:hidden flex-col gap-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  user={user}
                  onCopy={handleCopy}
                  onMarkUsed={handleMarkUsed}
                  onSkip={handleSkip}
                  onEdit={handleEditPost}
                  onStar={handleStar}
                  copiedId={copiedId}
                  starredIds={starredIds}
                />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8"
                >
                  {isLoadingMore ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                  ) : (
                    "Load More Posts"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
};

export default FeedPage;
